import React, { useState, useRef, useEffect } from "react";
import styles from "./EventManagement.module.css";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiService } from "../../services/api";
import { Event, EventPhoto, Shop } from "../../types";
import { useNavigate } from "react-router-dom";

interface FormState {
  id?: string;
  nameRu: string;
  nameEn?: string;
  shortDescriptionRu?: string;
  shortDescriptionEn?: string;
  fullDescriptionRu?: string;
  fullDescriptionEn?: string;
  locationRu?: string;
  locationEn?: string;
  workingHoursRu?: string;
  workingHoursEn?: string;
  searchKeywords?: string;
  instagram?: string;
  facebook?: string;
  twitter?: string;
  tiktok?: string;
  youtube?: string;
  telegram?: string;
  logoUrl?: string;
  shopId?: number;
  isActive: boolean;
}

export default function EventManagement() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const modalContentRef = useRef<HTMLDivElement>(null);

  const { data: events, isLoading } = useQuery({
    queryKey: ["events"],
    queryFn: () => apiService.getEvents(),
  });

  const { data: shops } = useQuery({
    queryKey: ["shops"],
    queryFn: () => apiService.getShops(),
  });

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formState, setFormState] = useState<FormState>({
    nameRu: "",
    isActive: true,
  });
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [eventPhotoFiles, setEventPhotoFiles] = useState<File[]>([]);
  const [eventPhotoPreviews, setEventPhotoPreviews] = useState<string[]>([]);
  const [existingPhotos, setExistingPhotos] = useState<EventPhoto[]>([]);
  const [filterName, setFilterName] = useState<string>("");
  const [filterShowInactiveOnly, setFilterShowInactiveOnly] =
    useState<boolean>(false);
  const [shopSearchQuery, setShopSearchQuery] = useState<string>("");
  const [isShopDropdownOpen, setIsShopDropdownOpen] = useState<boolean>(false);
  const [formError, setFormError] = useState<string>("");

  const resetForm = () => {
    setFormState({
      nameRu: "",
      isActive: true,
    });
    setLogoPreview(null);
    setEventPhotoFiles([]);
    setEventPhotoPreviews([]);
    setExistingPhotos([]);
    setShopSearchQuery("");
    setIsShopDropdownOpen(false);
    setFormError("");
  };

  const openCreate = () => {
    resetForm();
    setFormError("");
    createMutation.reset();
    updateMutation.reset();
    setIsFormOpen(true);
  };

  const openEdit = (event: Event) => {
    setFormState({
      id: event.id,
      nameRu: event.nameRu || "",
      nameEn: event.nameEn || "",
      shortDescriptionRu: event.shortDescriptionRu || "",
      shortDescriptionEn: event.shortDescriptionEn || "",
      fullDescriptionRu: event.fullDescriptionRu || "",
      fullDescriptionEn: event.fullDescriptionEn || "",
      locationRu: event.locationRu || "",
      locationEn: event.locationEn || "",
      workingHoursRu: event.workingHoursRu || "",
      workingHoursEn: event.workingHoursEn || "",
      searchKeywords: event.searchKeywords || "",
      instagram: event.socialMedia?.instagram || "",
      facebook: event.socialMedia?.facebook || "",
      twitter: event.socialMedia?.twitter || "",
      tiktok: event.socialMedia?.tiktok || "",
      youtube: event.socialMedia?.youtube || "",
      telegram: event.socialMedia?.telegram || "",
      logoUrl: event.logoUrl || "",
      shopId: event.shopId,
      isActive: event.isActive,
    });
    setLogoPreview(
      event.logoUrl
        ? `${process.env.REACT_APP_MEDIA_PATH}${event.logoUrl}`
        : null
    );
    setExistingPhotos(event.photos || []);
    // Устанавливаем название объекта в поле поиска, если объект выбран
    if (event.shopId && shops) {
      const shop = shops.find((s: Shop) => Number(s.id) === event.shopId);
      setShopSearchQuery(shop?.nameRu || "");
    } else {
      setShopSearchQuery("");
    }
    setFormError("");
    createMutation.reset();
    updateMutation.reset();
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    resetForm();
    setFormError("");
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIsUploadingLogo(true);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      // Upload logo
      try {
        const result = await apiService.uploadEventLogo(file);
        setFormState({ ...formState, logoUrl: result.url });
      } catch (error: any) {
        alert(error.response?.data?.message || "Ошибка при загрузке логотипа");
      } finally {
        setIsUploadingLogo(false);
      }
    }
  };

  const handleEventPhotosChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setEventPhotoFiles(files);
    const readers: Promise<string>[] = files.map(
      (file) =>
        new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(file);
        })
    );
    Promise.all(readers).then((results) => setEventPhotoPreviews(results));
  };

  const createMutation = useMutation({
    mutationFn: async (payload: FormState) => {
      // Pass data in frontend format (apiService.createEvent will convert to backend format)
      const body: any = {
        nameRu: payload.nameRu,
        nameEn: payload.nameEn,
        shortDescriptionRu: payload.shortDescriptionRu,
        shortDescriptionEn: payload.shortDescriptionEn,
        fullDescriptionRu: payload.fullDescriptionRu,
        fullDescriptionEn: payload.fullDescriptionEn,
        locationRu: payload.locationRu,
        locationEn: payload.locationEn,
        workingHoursRu: payload.workingHoursRu,
        workingHoursEn: payload.workingHoursEn,
        searchKeywords: payload.searchKeywords,
        socialMedia:
          payload.instagram ||
          payload.facebook ||
          payload.twitter ||
          payload.tiktok ||
          payload.youtube ||
          payload.telegram
            ? {
                instagram: payload.instagram || undefined,
                facebook: payload.facebook || undefined,
                twitter: payload.twitter || undefined,
                tiktok: payload.tiktok || undefined,
                youtube: payload.youtube || undefined,
                telegram: payload.telegram || undefined,
              }
            : undefined,
        logoUrl: payload.logoUrl || undefined,
        shopId: payload.shopId || undefined,
        isActive: payload.isActive !== undefined ? payload.isActive : true,
      };
      const event = await apiService.createEvent(body);

      // Upload photos if any
      if (eventPhotoFiles.length > 0) {
        await apiService.uploadEventPhotos(event.id, eventPhotoFiles);
      }

      return event;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      closeForm();
    },
    onError: (error: any) => {
      console.error("Error creating event:", error);
      const validationErrors = error?.response?.data?.errors;
      if (
        validationErrors &&
        Array.isArray(validationErrors) &&
        validationErrors.length > 0
      ) {
        const errorMessages = validationErrors.map((err: any) => {
          const fieldName = err.param || "Поле";
          const message = err.msg || "Ошибка валидации";
          return `• ${fieldName}: ${message}`;
        });
        setFormError(`Ошибки валидации:\n${errorMessages.join("\n")}`);
      } else {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Не удалось создать событие";
        setFormError(errorMessage);
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: FormState) => {
      if (!payload.id) throw new Error("Missing id");
      // Send data in frontend format (apiService.updateEvent will convert it to backend format)
      const body: any = {
        nameRu: payload.nameRu,
        nameEn: payload.nameEn,
        shortDescriptionRu: payload.shortDescriptionRu,
        shortDescriptionEn: payload.shortDescriptionEn,
        fullDescriptionRu: payload.fullDescriptionRu,
        fullDescriptionEn: payload.fullDescriptionEn,
        locationRu: payload.locationRu,
        locationEn: payload.locationEn,
        workingHoursRu: payload.workingHoursRu,
        workingHoursEn: payload.workingHoursEn,
        searchKeywords: payload.searchKeywords,
        socialMedia:
          payload.instagram ||
          payload.facebook ||
          payload.twitter ||
          payload.tiktok ||
          payload.youtube ||
          payload.telegram
            ? {
                instagram: payload.instagram || undefined,
                facebook: payload.facebook || undefined,
                twitter: payload.twitter || undefined,
                tiktok: payload.tiktok || undefined,
                youtube: payload.youtube || undefined,
                telegram: payload.telegram || undefined,
              }
            : undefined,
        logoUrl: payload.logoUrl,
        shopId: payload.shopId,
        isActive: payload.isActive,
      };
      const event = await apiService.updateEvent(payload.id, body);

      // Upload new photos if any
      if (eventPhotoFiles.length > 0) {
        await apiService.uploadEventPhotos(event.id, eventPhotoFiles);
      }

      return event;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      closeForm();
    },
    onError: (error: any) => {
      console.error("Error updating event:", error);
      const validationErrors = error?.response?.data?.errors;
      if (
        validationErrors &&
        Array.isArray(validationErrors) &&
        validationErrors.length > 0
      ) {
        const errorMessages = validationErrors.map((err: any) => {
          const fieldName = err.param || "Поле";
          const message = err.msg || "Ошибка валидации";
          return `• ${fieldName}: ${message}`;
        });
        setFormError(`Ошибки валидации:\n${errorMessages.join("\n")}`);
      } else {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Не удалось обновить событие";
        setFormError(errorMessage);
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => apiService.deleteEvent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
    },
  });

  const deletePhotoMutation = useMutation({
    mutationFn: async (photoId: string) => apiService.deleteEventPhoto(photoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
    },
  });

  const getShopNameRu = (shopId?: number) => {
    if (!shopId || !shops) return "—";
    const shop = shops.find((s: Shop) => Number(s.id) === shopId);
    return shop ? shop.nameRu : String(shopId);
  };

  const filteredShops =
    shops?.filter((shop: Shop) =>
      shop.nameRu.toLowerCase().includes(shopSearchQuery.toLowerCase())
    ) || [];

  const selectedShop = shops?.find(
    (shop: Shop) => Number(shop.id) === formState.shopId
  );

  const handleShopSelect = (shopId: number | undefined) => {
    setFormState((s) => ({ ...s, shopId }));
    if (shopId && shops) {
      const shop = shops.find((s: Shop) => Number(s.id) === shopId);
      setShopSearchQuery(shop?.nameRu || "");
    } else {
      setShopSearchQuery("");
    }
    setIsShopDropdownOpen(false);
    // Очищаем ошибку при выборе объекта
    if (shopId) {
      setFormError("");
    }
  };

  const handleShopInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setShopSearchQuery(value);
    setIsShopDropdownOpen(true);
    // Если поле очищено, сбрасываем выбор
    if (value === "") {
      setFormState((s) => ({ ...s, shopId: undefined }));
      setFormError("");
    }
  };

  const handleShopInputFocus = () => {
    setIsShopDropdownOpen(true);
    if (!shopSearchQuery && selectedShop) {
      setShopSearchQuery(selectedShop.nameRu);
    }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const validationMessages: string[] = [];

    // Валидация обязательного поля "Объект"
    if (!formState.shopId) {
      validationMessages.push("• Объект: обязательно для заполнения");
    }

    if (validationMessages.length > 0) {
      setFormError(`Ошибки валидации:\n${validationMessages.join("\n")}`);
      return;
    }

    setFormError("");

    if (formState.id) {
      updateMutation.mutate(formState);
    } else {
      createMutation.mutate(formState);
    }
  };

  const handleDelete = (event: Event) => {
    if (window.confirm(`Удалить событие «${event.nameRu}»?`)) {
      deleteMutation.mutate(event.id);
    }
  };

  const handleDeletePhoto = (photoId: string) => {
    if (window.confirm("Удалить это фото?")) {
      // Удаляем фото из локального состояния сразу
      setExistingPhotos((prev) => prev.filter((photo) => photo.id !== photoId));
      deletePhotoMutation.mutate(photoId);
    }
  };

  const handleDeleteNewPhoto = (index: number) => {
    // Удаляем фото из preview и из списка файлов
    setEventPhotoPreviews((prev) => prev.filter((_, i) => i !== index));
    setEventPhotoFiles((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (isFormOpen && modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
  }, [isFormOpen]);

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.logo}>Управление событиями</div>
        <button
          className={styles.backButton}
          onClick={() => navigate("/admin")}
        >
          Назад
        </button>
      </header>

      <main className={styles.mainContent}>
        <h1 className={styles.pageTitle}>События</h1>

        <div className={styles.controlsRow}>
          <button className={styles.addButton} onClick={openCreate}>
            + Добавить событие
          </button>
          <div className={styles.filterContainer}>
            <input
              type="text"
              className={styles.filterInput}
              placeholder="Поиск по названию события..."
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
            />
          </div>
          <div className={styles.statusFilterContainer}>
            <div className={styles.checkboxContainer}>
              <input
                className={styles.checkboxInput}
                type="checkbox"
                id="filterShowInactiveOnly"
                checked={filterShowInactiveOnly}
                onChange={(e) => setFilterShowInactiveOnly(e.target.checked)}
              />
              <label
                className={styles.checkboxLabel}
                htmlFor="filterShowInactiveOnly"
              >
                Неактивные
              </label>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className={styles.loadingSpinner}>Загрузка...</div>
        ) : (
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th className={styles.tableHeaderCell}>Название (RU)</th>
                <th className={styles.tableHeaderCell}>Название (EN)</th>
                <th className={styles.tableHeaderCell}>Объект</th>
                {/* <th className={styles.tableHeaderCell}>Статус</th> */}
                <th className={styles.tableHeaderCell}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {events && events.length > 0 ? (
                events
                  .filter((e) => {
                    const matchesName = e.nameRu
                      .toLowerCase()
                      .includes(filterName.toLowerCase().trim());
                    const matchesActiveStatus = filterShowInactiveOnly
                      ? !e.isActive
                      : e.isActive;
                    return matchesName && matchesActiveStatus;
                  })
                  .map((e) => (
                    <tr key={e.id} className={styles.tableRow}>
                      <td className={styles.tableCell}>{e.nameRu}</td>
                      <td className={styles.tableCell}>{e.nameEn || "—"}</td>
                      <td className={styles.tableCell}>
                        {getShopNameRu(e.shopId)}
                      </td>
                      {/* <td className={styles.tableCell}>
                        <span
                          className={`${styles.badge} ${
                            e.isActive
                              ? styles.badgeActive
                              : styles.badgeInactive
                          }`}
                        >
                          {e.isActive ? "Активен" : "Неактивен"}
                        </span>
                      </td> */}
                      <td className={styles.tableCell}>
                        <button
                          className={styles.actionButton}
                          onClick={() => openEdit(e)}
                        >
                          Редактировать
                        </button>
                        <button
                          className={`${styles.actionButton} ${styles.actionDanger}`}
                          onClick={() => handleDelete(e)}
                        >
                          Удалить
                        </button>
                      </td>
                    </tr>
                  ))
              ) : (
                <tr>
                  <td className={styles.tableCell} colSpan={5}>
                    События отсутствуют
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        <div
          className={`${styles.modal} ${isFormOpen ? styles.modalOpen : ""}`}
        >
          <div
            ref={modalContentRef}
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className={styles.modalTitle}>
              {formState.id ? "Редактировать событие" : "Создать событие"}
            </h2>
            {formError && (
              <div
                className={styles.errorMessage}
                style={{ whiteSpace: "pre-line" }}
              >
                {formError}
              </div>
            )}
            <form onSubmit={onSubmit}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Название (RU) *</label>
                <input
                  className={styles.input}
                  value={formState.nameRu}
                  onChange={(e) =>
                    setFormState((s) => ({ ...s, nameRu: e.target.value }))
                  }
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Название (EN)</label>
                <input
                  className={styles.input}
                  value={formState.nameEn || ""}
                  onChange={(e) =>
                    setFormState((s) => ({ ...s, nameEn: e.target.value }))
                  }
                />
              </div>
              {/* <div className={styles.formGroup}>
                <label className={styles.label}>Краткое описание (RU)</label>
                <textarea
                  className={styles.textArea}
                  value={formState.shortDescriptionRu || ""}
                  onChange={(e) =>
                    setFormState((s) => ({
                      ...s,
                      shortDescriptionRu: e.target.value,
                    }))
                  }
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Краткое описание (EN)</label>
                <textarea
                  className={styles.textArea}
                  value={formState.shortDescriptionEn || ""}
                  onChange={(e) =>
                    setFormState((s) => ({
                      ...s,
                      shortDescriptionEn: e.target.value,
                    }))
                  }
                />
              </div> */}
              <div className={styles.formGroup}>
                <label className={styles.label}>Полное описание (RU)</label>
                <textarea
                  className={styles.textArea}
                  value={formState.fullDescriptionRu || ""}
                  onChange={(e) =>
                    setFormState((s) => ({
                      ...s,
                      fullDescriptionRu: e.target.value,
                    }))
                  }
                  rows={4}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Полное описание (EN)</label>
                <textarea
                  className={styles.textArea}
                  value={formState.fullDescriptionEn || ""}
                  onChange={(e) =>
                    setFormState((s) => ({
                      ...s,
                      fullDescriptionEn: e.target.value,
                    }))
                  }
                  rows={4}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Местоположение (RU)</label>
                <input
                  className={styles.input}
                  value={formState.locationRu || ""}
                  onChange={(e) =>
                    setFormState((s) => ({
                      ...s,
                      locationRu: e.target.value,
                    }))
                  }
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Местоположение (EN)</label>
                <input
                  className={styles.input}
                  value={formState.locationEn || ""}
                  onChange={(e) =>
                    setFormState((s) => ({
                      ...s,
                      locationEn: e.target.value,
                    }))
                  }
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Часы работы (RU)</label>
                <input
                  className={styles.input}
                  value={formState.workingHoursRu || ""}
                  onChange={(e) =>
                    setFormState((s) => ({
                      ...s,
                      workingHoursRu: e.target.value,
                    }))
                  }
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Часы работы (EN)</label>
                <input
                  className={styles.input}
                  value={formState.workingHoursEn || ""}
                  onChange={(e) =>
                    setFormState((s) => ({
                      ...s,
                      workingHoursEn: e.target.value,
                    }))
                  }
                />
              </div>
              {/* <div className={styles.formGroup}>
                <label className={styles.label}>
                  Ключевые слова для поиска
                </label>
                <input
                  className={styles.input}
                  value={formState.searchKeywords || ""}
                  onChange={(e) =>
                    setFormState((s) => ({
                      ...s,
                      searchKeywords: e.target.value,
                    }))
                  }
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Instagram</label>
                <input
                  className={styles.input}
                  value={formState.instagram || ""}
                  onChange={(e) =>
                    setFormState((s) => ({
                      ...s,
                      instagram: e.target.value,
                    }))
                  }
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Facebook</label>
                <input
                  className={styles.input}
                  value={formState.facebook || ""}
                  onChange={(e) =>
                    setFormState((s) => ({
                      ...s,
                      facebook: e.target.value,
                    }))
                  }
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Twitter</label>
                <input
                  className={styles.input}
                  value={formState.twitter || ""}
                  onChange={(e) =>
                    setFormState((s) => ({
                      ...s,
                      twitter: e.target.value,
                    }))
                  }
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>TikTok</label>
                <input
                  className={styles.input}
                  value={formState.tiktok || ""}
                  onChange={(e) =>
                    setFormState((s) => ({
                      ...s,
                      tiktok: e.target.value,
                    }))
                  }
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>YouTube</label>
                <input
                  className={styles.input}
                  value={formState.youtube || ""}
                  onChange={(e) =>
                    setFormState((s) => ({
                      ...s,
                      youtube: e.target.value,
                    }))
                  }
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Telegram</label>
                <input
                  className={styles.input}
                  value={formState.telegram || ""}
                  onChange={(e) =>
                    setFormState((s) => ({
                      ...s,
                      telegram: e.target.value,
                    }))
                  }
                />
              </div> */}
              <div className={styles.formGroup}>
                <label className={styles.label}>Логотип</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  disabled={isUploadingLogo}
                />
                {logoPreview && (
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    className={styles.previewImage}
                  />
                )}
                {isUploadingLogo && <p>Загрузка...</p>}
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Объект*</label>
                <div className={styles.shopSelectWrapper}>
                  <input
                    type="text"
                    className={styles.shopSelectInput}
                    placeholder="Поиск объекта..."
                    value={shopSearchQuery}
                    onChange={handleShopInputChange}
                    onFocus={handleShopInputFocus}
                    onBlur={() => {
                      // Задержка для обработки клика по элементу списка
                      setTimeout(() => setIsShopDropdownOpen(false), 200);
                    }}
                  />
                  {isShopDropdownOpen && (
                    <div className={styles.shopDropdown}>
                      <div
                        className={styles.shopDropdownItem}
                        onClick={() => handleShopSelect(undefined)}
                      >
                        —
                      </div>
                      {filteredShops.length > 0 ? (
                        filteredShops.map((shop: Shop) => (
                          <div
                            key={shop.id}
                            className={`${styles.shopDropdownItem} ${
                              formState.shopId === Number(shop.id)
                                ? styles.shopDropdownItemSelected
                                : ""
                            }`}
                            onClick={() => handleShopSelect(Number(shop.id))}
                          >
                            {shop.nameRu}
                          </div>
                        ))
                      ) : (
                        <div className={styles.shopDropdownItemEmpty}>
                          Объекты не найдены
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {/* <div className={styles.formGroup}>
                <label className={styles.label}>
                  <input
                    type="checkbox"
                    checked={formState.isActive}
                    onChange={(e) =>
                      setFormState((s) => ({
                        ...s,
                        isActive: e.target.checked,
                      }))
                    }
                  />
                  Активно
                </label>
              </div> */}
              <div className={styles.formGroup}>
                <label className={styles.label}>Фотографии</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleEventPhotosChange}
                  disabled={
                    createMutation.isPending || updateMutation.isPending
                  }
                />
                {(createMutation.isPending || updateMutation.isPending) &&
                  eventPhotoFiles.length > 0 && <p>Загрузка...</p>}
              </div>
              {eventPhotoPreviews.length > 0 && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Предпросмотр новых фотографий
                  </label>
                  <div className={styles.photoPreviewGrid}>
                    {eventPhotoPreviews.map((preview, index) => (
                      <div key={index} className={styles.photoPreviewItem}>
                        <img
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          className={styles.previewImage}
                        />
                        <button
                          type="button"
                          className={`${styles.actionButton} ${styles.actionDanger}`}
                          onClick={() => handleDeleteNewPhoto(index)}
                        >
                          Удалить
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {existingPhotos.length > 0 && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Существующие фотографии
                  </label>
                  <div className={styles.photoPreviewGrid}>
                    {existingPhotos.map((photo) => (
                      <div key={photo.id} className={styles.photoPreviewItem}>
                        <img
                          src={`${process.env.REACT_APP_MEDIA_PATH}${photo.url}`}
                          alt="Event photo"
                          className={styles.previewImage}
                        />
                        <button
                          type="button"
                          className={`${styles.actionButton} ${styles.actionDanger}`}
                          onClick={() => handleDeletePhoto(photo.id)}
                        >
                          Удалить
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={`${styles.actionButton} ${styles.actionWarning}`}
                  onClick={closeForm}
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className={styles.actionButton}
                  disabled={
                    createMutation.isPending ||
                    updateMutation.isPending ||
                    isUploadingLogo
                  }
                >
                  {formState.id ? "Сохранить" : "Создать"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
